import { PropsWithChildren, useEffect, useState } from "react";
import { ColorOverlayFilter } from "pixi-filters";
import BoundingBox from "../../Domain/Types/BoundingBox.type";
import { PlaceVariant } from "../../Domain/Types/FloorPlan/PlaceVariant.type";
import { ParkPlaceSprite } from "./Sprite/ParkPlaceSprite";
import { WorkPlaceSprite } from "./Sprite/WorkPlaceSprite";
import { ToolSelection } from "../Views/CreateFloorPlanView/CreateFloorPlanView";
import { BrowserType, getBrowser } from "./functions/detectBrowser.function";

interface Props {
  boundingBox: BoundingBox;
  isSelected?: boolean;
  colorOverlay?: number;
  isHighlighted?: boolean;
  isFavorite?: boolean;
  isSelectable: boolean;
  variant: PlaceVariant;
  placeTypeId: number | undefined;
  placeScale: number;
  icon?: string;
  tool?: ToolSelection;
  isColleaguesPlace?: boolean;
}

export default function PlaceSprite({
  boundingBox,
  isSelected = false,
  colorOverlay,
  isHighlighted,
  isFavorite = false,
  isSelectable,
  variant,
  placeTypeId = 1,
  placeScale,
  icon,
  tool,
  isColleaguesPlace
}: Readonly<PropsWithChildren<Props>>) {
  //
  // Color
  //
  const selectedColor = 0x0027eb;
  const defaultDeskColor = 0x28b869;
  const occupiedDeskColor = 0xff0000;
  const disabledDeskColor = 0xd2dae1;
  const forbiddenDeskColor = 0xd2dae1; // 0xca61cb;
  const tempDeskColor = 0xdddd56;

  const selectedColorFilter = [new ColorOverlayFilter(selectedColor, 0.6)];
  const [selectedFilter, setSelectedFilter] = useState<ColorOverlayFilter[]>([]);

  const variantColor = (value: PlaceVariant) => {
    switch (value) {
      case PlaceVariant.AVAILABLE:
        return defaultDeskColor;
      case PlaceVariant.OCCUPIED:
        return occupiedDeskColor;
      case PlaceVariant.WEAK:
        return disabledDeskColor;
      case PlaceVariant.FORBIDDEN:
        return forbiddenDeskColor;
      case PlaceVariant.TEMP:
        return tempDeskColor;
    }
  };

  /**
   * deprecated capacity colorOverlay
   * @used const capacityFilters = useMemo(() => { if (!colorOverlay) return []; return [new ColorOverlayFilter(colorOverlay, 0.6)]; }, [colorOverlay]);
   *
   * Color Overlay Filter to show capacity rates, used in the ReportFloorPlan
   * and using itself is expensive and much more because it covers all the places which has capacity rates
   *
   * deprecated const highlightedColor = 0xeeee00;
   * @used const highlightFilter = isHighlighted ? [new ColorOverlayFilter(highlightedColor, 0.2)] : [];
   *
   * Color Overlay Filter is applied to places which are belonged to the selected zone, used in the CreateFloorPlan and ReportFloorPlan
   *
   * @before const filterGroup = [...capacityFilters, ...highlightFilter, ...selectedFilter];
   * @summary it is replaced by normal color fill to improve performance
   */
  const filterGroup = [...selectedFilter];

  useEffect(() => {
    if (isSelected) {
      setSelectedFilter(selectedColorFilter);
      return;
    }
    setSelectedFilter([]);
  }, [isSelected]);

  /**
   * @description when user uses Firefox, there's an issue that difficulty to draw WebGL, particularly svg image of PIXI.Sprite
   * therefore, for now, when Firefox is used, we draw place sprite as Sprite, not SVG Image.
   * we can change this way to add width and height of svg image, which can make svg rendering in Firefox
   */
  const [isGrpPreferred, setIsGrpPreferred] = useState(false);

  useEffect(() => {
    const usedBrowser = getBrowser(navigator.userAgent);
    if (usedBrowser === BrowserType.FIREFOX) setIsGrpPreferred(true);
  }, []);

  return (
    <CreatePlaceSprite
      placeTypeId={placeTypeId}
      placeScale={placeScale}
      boundingBox={boundingBox}
      filterGroup={filterGroup}
      variantColor={variantColor(variant)}
      variant={variant}
      isSelectable={isSelectable}
      tool={tool}
      icon={icon}
      isColleaguesPlace={isColleaguesPlace}
      isHighlighted={isHighlighted}
      isFavorite={isFavorite}
      colorOverlay={colorOverlay}
      isGrpPreferred={isGrpPreferred}
    />
  );
}

type CreatePlaceSpriteProps = {
  placeTypeId: number;
  placeScale: number;
  boundingBox: BoundingBox;
  filterGroup: ColorOverlayFilter[];
  variantColor: number;
  variant: PlaceVariant;
  isSelectable: boolean;
  isHighlighted?: boolean;
  isFavorite?: boolean;
  colorOverlay: number | undefined;
  tool?: ToolSelection;
  icon?: string;
  isColleaguesPlace?: boolean;
  isGrpPreferred: boolean;
};

export function CreatePlaceSprite({
  placeTypeId,
  placeScale,
  boundingBox,
  filterGroup,
  variantColor,
  variant,
  isSelectable,
  isHighlighted,
  isFavorite = false,
  colorOverlay,
  tool,
  icon,
  isColleaguesPlace,
  isGrpPreferred
}: Readonly<CreatePlaceSpriteProps>) {
  // this sprite is shared for park place and electric charging station
  const parkPlaceSprite = (
    <ParkPlaceSprite
      placeTypeId={placeTypeId}
      boundingBox={boundingBox}
      filterGroup={filterGroup}
      variantColor={variantColor}
      variant={variant}
      placeScale={placeScale}
      isSelectable={isSelectable}
      icon={icon}
      tool={tool}
      isColleaguesPlace={isColleaguesPlace}
      isHighlighted={isHighlighted}
      isFavorite={isFavorite}
      colorOverlay={colorOverlay}
      isGrpPreferred={isGrpPreferred}
    />
  );

  const workPlaceSprite = (
    <WorkPlaceSprite
      boundingBox={boundingBox}
      filterGroup={filterGroup}
      variantColor={variantColor}
      variant={variant}
      placeScale={placeScale}
      isSelectable={isSelectable}
      tool={tool}
      isColleaguesPlace={isColleaguesPlace}
      isHighlighted={isHighlighted}
      isFavorite={isFavorite}
      colorOverlay={colorOverlay}
      isGrpPreferred={isGrpPreferred}
    />
  );

  switch (placeTypeId) {
    case 1: // normal workplace type
      return workPlaceSprite;
    case 2: // car parking place
      return parkPlaceSprite;
    case 4: // electric charging station
      return parkPlaceSprite;
    default:
      return null;
  }
}
